import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Video, Microscope, PenTool, Lightbulb, BookOpen, Play, Check, X, Loader2, GraduationCap } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { chapterAnnotationsAPI } from '@/api/chapterAnnotations';
import { userActivitiesAPI } from '@/api/userActivities';
import SimulationRenderer from '@/components/simulations/SimulationRenderer';
import WatchContentRenderer from '@/components/watch/WatchContentRenderer';
import BasicConceptsRenderer from '@/components/basicconcepts/BasicConceptsRenderer';
import { toast } from 'sonner';
import { usePoints } from '@/context/PointsContext';
import FloatingPointsAnimation, { useFloatingPointsAnimation } from '@/components/FloatingPointsAnimation';
import BookmarkIcon from './BookmarkIcon';
import { bookmarksAPI } from '@/api/bookmarks';

// Helper function to get video error messages
function getVideoErrorMessage(errorCode) {
  switch (errorCode) {
    case 1: return 'MEDIA_ERR_ABORTED - Video loading aborted';
    case 2: return 'MEDIA_ERR_NETWORK - Network error while loading video';
    case 3: return 'MEDIA_ERR_DECODE - Video decoding error';
    case 4: return 'MEDIA_ERR_SRC_NOT_SUPPORTED - Video format not supported';
    default: return 'Unknown error';
  }
}

// Helper function to check if URL is a YouTube URL
function isYouTubeUrl(url) {
  if (!url) return false;
  return url.includes('youtube.com') ||
    url.includes('youtu.be') ||
    url.startsWith('https://www.youtube.com') ||
    url.startsWith('https://youtu.be');
}

// YouTube Video Player Component
function YouTubeVideoPlayer({ youtubeUrl, onEnded, onTimeUpdate, playbackRate = 1 }) {
  const iframeRef = useRef(null);
  const [videoId, setVideoId] = useState(null);
  const [player, setPlayer] = useState(null);
  const intervalRef = useRef(null);

  // Extract YouTube video ID from URL
  useEffect(() => {
    if (!youtubeUrl) return;

    const extractVideoId = (url) => {
      if (!url) return null;

      const patterns = [
        /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
          return match[1];
        }
      }

      // If no pattern matches, assume the URL itself is the ID (if it's 11 chars)
      if (url.length === 11 && /^[a-zA-Z0-9_-]+$/.test(url)) {
        return url;
      }

      return null;
    };

    const id = extractVideoId(youtubeUrl);
    setVideoId(id);
  }, [youtubeUrl]);

  // Load YouTube IFrame API
  useEffect(() => {
    if (!videoId) return;

    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      initializePlayer();
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
          initializePlayer();
        };
      }
      return;
    }

    // Load the script
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      initializePlayer();
    };

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (player) {
        try {
          player.destroy();
        } catch (e) {
          console.error('Error destroying YouTube player:', e);
        }
      }
    };
  }, [videoId]);

  const initializePlayer = () => {
    if (!videoId || !iframeRef.current || !window.YT || !window.YT.Player) {
      return;
    }

    try {
      const ytPlayer = new window.YT.Player(iframeRef.current, {
        videoId: videoId,
        playerVars: {
          // Don't force mute; user should control sound. Autoplay disabled to respect browser policies.
          autoplay: 0,
          mute: 0,
          controls: 1,
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
          playsinline: 1,
        },
        events: {
          onReady: (event) => {
            try {
              // Ensure unmuted by default and apply playback rate
              if (event?.target?.unMute) event.target.unMute();
              if (event?.target?.setPlaybackRate) event.target.setPlaybackRate(playbackRate || 1);
            } catch (e) {
              // ignore
            }
            setPlayer(event.target);
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              // Start tracking time
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
              }
              intervalRef.current = setInterval(() => {
                if (event.target && onTimeUpdate) {
                  try {
                    const currentTime = event.target.getCurrentTime();
                    const duration = event.target.getDuration();
                    if (duration > 0) {
                      const percentage = (currentTime / duration) * 100;
                      onTimeUpdate(percentage);
                    }
                  } catch (e) {
                    // Ignore errors
                  }
                }
              }, 1000);
            } else if (event.data === window.YT.PlayerState.PAUSED) {
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
              }
            } else if (event.data === window.YT.PlayerState.ENDED) {
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
              }
              if (onEnded) {
                onEnded();
              }
            }
          },
          onError: (event) => {
            console.error('YouTube player error:', event.data);
            toast.error('Failed to load YouTube video. Please check the video URL.');
          },
        },
      });
    } catch (error) {
      console.error('Error initializing YouTube player:', error);
      toast.error('Failed to initialize YouTube player.');
    }
  };

  // Adjust playback rate if requested after initialization
  useEffect(() => {
    if (player && typeof player.setPlaybackRate === 'function') {
      try {
        player.setPlaybackRate(playbackRate || 1);
      } catch (e) {
        // ignore
      }
    }
  }, [playbackRate, player]);

  if (!videoId) {
    return (
      <div className="text-center text-gray-400 py-8">
        <p>Invalid YouTube URL</p>
      </div>
    );
  }

  return (
    <div ref={iframeRef} className="w-full h-full" />
  );
}

const iconMap = {
  Watch: BookOpen, // Use BookOpen icon for Learn & Discover
  Video: Video, // Use Video icon for Video type
  Explore: Microscope,
  Practice: PenTool,
  Curiosity: Lightbulb,
  BasicConcepts: GraduationCap // Use GraduationCap icon for Basic Concepts
};

const typeColors = {
  Watch: 'from-blue-500 to-purple-600',
  Video: 'from-red-500 via-pink-500 to-purple-500', // Video type has red-pink-purple gradient
  Explore: 'from-green-500 to-teal-600',
  Practice: 'from-orange-500 to-red-600',
  Curiosity: 'from-yellow-500 to-amber-600',
  BasicConcepts: 'from-indigo-500 via-purple-500 to-pink-500' // Basic Concepts gradient
};

export default function ActivityModal({ item, isOpen, onClose, onComplete, isAlreadyCompleted, chapterId, onNext, onPrev, hasNext, hasPrev, onBookmarkChange }) {
  const { refreshPoints } = usePoints();
  const { addAnimation, renderAnimations } = useFloatingPointsAnimation();
  const modalContentRef = useRef(null);

  const [activityState, setActivityState] = useState('idle'); // idle, in-progress, completed
  const [timer, setTimer] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [clickedElements, setClickedElements] = useState(new Set());
  const [practiceQuestions, setPracticeQuestions] = useState(null);

  // Handle modal close - kill celebration animations immediately
  const handleClose = () => {
    // Dispatch global event to kill all celebration animations
    window.dispatchEvent(new Event('close-celebration'));
    // Call the original onClose handler
    onClose && onClose();
  };
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState(null);
  const [practiceSubmitted, setPracticeSubmitted] = useState(false);
  const [practiceAttempts, setPracticeAttempts] = useState({}); // Track attempts per question
  const [showCorrectAnswers, setShowCorrectAnswers] = useState({}); // Track if correct answer should be shown
  const [curiosityContent, setCuriosityContent] = useState(null); // Curiosity content from backend
  const [loadingCuriosity, setLoadingCuriosity] = useState(false);
  const [curiosityError, setCuriosityError] = useState(null);
  const [curiosityStep, setCuriosityStep] = useState(0); // 0: Question, 1: Quick idea, 2: Explanation
  const [curiosityReveal, setCuriosityReveal] = useState(false);
  const [exploreSimulation, setExploreSimulation] = useState(null); // Explore simulation from backend
  const [loadingSimulation, setLoadingSimulation] = useState(false);
  const [simulationError, setSimulationError] = useState(null);
  const [watchContent, setWatchContent] = useState(null); // Watch content from backend
  const [loadingWatch, setLoadingWatch] = useState(false);
  const [watchError, setWatchError] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const videoRef = useRef(null); // Ref for video element
  const [playbackRate, setPlaybackRate] = useState(1);
  const [viewerBookmark, setViewerBookmark] = useState(null);

  // Fetch bookmarks to check if current activity is bookmarked
  useEffect(() => {
    if (isOpen && chapterId) {
      bookmarksAPI.getChapterBookmarks(chapterId)
        .then((bookmarks) => {
          const contentId = String(item.id || item.annotation_id);
          const found = bookmarks.find((b) =>
            String(b.content_id) === contentId &&
            b.bookmark_type === 'activity'
          );
          setViewerBookmark(found || null);
        })
        .catch((err) => {
          console.error('Error fetching bookmarks:', err);
        });
    } else {
      setViewerBookmark(null);
    }
  }, [isOpen, chapterId, item?.id, item?.annotation_id]);

  // Ensure HTML5 video is unmuted and respects playback rate
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      try {
        v.muted = false;
        v.volume = 1;
        v.playbackRate = playbackRate || 1;
      } catch (e) {
        // ignore
      }
    }
  }, [playbackRate]);

  // Scoring tracking
  const [performanceScore, setPerformanceScore] = useState(null);
  const [completionTime, setCompletionTime] = useState(null);
  const [isFirstTry, setIsFirstTry] = useState(true);
  const [videoWatchPercentage, setVideoWatchPercentage] = useState(null);
  const startTimeRef = useRef(null);

  // Helper to get StatsBar position (top-right corner, typical position)
  const getStatsBarPosition = () => {
    // Try to find StatsBar in DOM
    const statsBar = document.querySelector('[data-stats-bar]');
    if (statsBar) {
      const rect = statsBar.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    }
    // Fallback to top-right corner
    return {
      x: window.innerWidth - 100,
      y: 60,
    };
  };

  // Helper to trigger floating animation
  const triggerFloatingAnimation = (type, amount) => {
    if (amount === 0) return;

    // Get start position (center of modal or center of screen)
    const startPosition = modalContentRef.current
      ? (() => {
        const rect = modalContentRef.current.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
      })()
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 };

    const targetPosition = getStatsBarPosition();

    addAnimation(type, amount, startPosition, targetPosition);
  };

  const Icon = iconMap[item?.type] || BookOpen;
  const colorGradient = typeColors[item?.type] || typeColors.Watch;

  // Fetch curiosity content from backend if this is a Curiosity annotation from backend
  useEffect(() => {
    if (isOpen && item?.type === 'Curiosity' && item?.annotation_id) {
      setLoadingCuriosity(true);
      setCuriosityError(null);
      setCuriosityContent(null);

      chapterAnnotationsAPI.getCuriosityContent(item.annotation_id)
        .then((data) => {
          setCuriosityContent(data);
          setLoadingCuriosity(false);
        })
        .catch((error) => {
          console.error('Error fetching curiosity content:', error);
          setCuriosityError(error.response?.data?.error || 'Failed to load curiosity content');
          setLoadingCuriosity(false);
        });
    } else {
      setCuriosityContent(null);
      setCuriosityError(null);
    }
  }, [isOpen, item?.type, item?.annotation_id]);

  useEffect(() => {
    if (!isOpen) return;
    if (item?.type !== 'Curiosity') return;
    setCuriosityStep(0);
    setCuriosityReveal(false);
  }, [isOpen, item?.type, item?.id, item?.annotation_id]);

  // Fetch explore simulation from backend if this is an Explore annotation from backend
  useEffect(() => {
    if (isOpen && item?.type === 'Explore' && item?.annotation_id) {
      setLoadingSimulation(true);
      setSimulationError(null);
      setExploreSimulation(null);

      chapterAnnotationsAPI.getExploreSimulation(item.annotation_id)
        .then((data) => {
          // Parse simulation_data if it's a string
          if (data && typeof data.simulation_data === 'string') {
            try {
              data.simulation_data = JSON.parse(data.simulation_data);
            } catch (e) {
              console.error('Error parsing simulation_data:', e);
              setSimulationError('Invalid simulation data format');
              setLoadingSimulation(false);
              return;
            }
          }
          setExploreSimulation(data);
          setLoadingSimulation(false);
        })
        .catch((error) => {
          console.error('Error fetching explore simulation:', error);
          setSimulationError(error.response?.data?.error || 'Failed to load simulation');
          setLoadingSimulation(false);
        });
    } else {
      setExploreSimulation(null);
      setSimulationError(null);
    }
  }, [isOpen, item?.type, item?.annotation_id]);

  useEffect(() => {
    // For Video or Watch with missing video_file_url, fetch annotation to get video URL
    if (isOpen && item?.annotation_id && (item?.type === 'Video' || item?.type === 'Watch') && !item?.video_file_url) {
      chapterAnnotationsAPI.getAnnotation(item.annotation_id)
        .then((data) => {
          if (data?.video_file_url) {
            setVideoUrl(data.video_file_url);
          }
        })
        .catch((error) => {
          console.error('Error fetching annotation for video url:', error);
        });
    } else {
      setVideoUrl(null);
    }
  }, [isOpen, item?.annotation_id, item?.type, item?.video_file_url]);
  // Fetch watch content from backend if this is a Watch annotation from backend
  // Skip if video_file_url is present (uploaded video takes priority)
  // Video type always shows uploaded video, never AI content
  useEffect(() => {
    if (isOpen && item?.type === 'Watch' && item?.annotation_id && !(item?.video_file_url || videoUrl)) {
      setLoadingWatch(true);
      setWatchError(null);
      setWatchContent(null);

      console.log('🔍 Fetching watch content for annotation:', item.annotation_id, 'Item:', item);
      chapterAnnotationsAPI.getWatchContent(item.annotation_id)
        .then((data) => {
          console.log('✅ Watch content loaded successfully:', data);
          setWatchContent(data);
          setLoadingWatch(false);
        })
        .catch((error) => {
          console.error('❌ Error fetching watch content:', {
            annotation_id: item.annotation_id,
            error: error.message,
            response: error.response?.data,
            status: error.response?.status,
            statusText: error.response?.statusText
          });
          const errorMessage = error.response?.data?.error || 'Failed to load watch content';

          // If content is still generating, show a friendly message
          if (error.response?.status === 404 && error.response?.data?.status === 'generating') {
            setWatchError('Watch content is still being generated. Please try again in a moment.');
          } else if (error.response?.status === 404) {
            // Annotation doesn't exist - might not have been saved yet
            setWatchError('This annotation may not be available yet. Please refresh the page.');
          } else {
            setWatchError(errorMessage);
          }
          setLoadingWatch(false);
        });
    } else {
      if (isOpen && item?.type === 'Watch') {
        if (!item?.annotation_id) {
          console.warn('⚠️ Watch annotation missing annotation_id:', item);
        }
        if (item?.video_file_url) {
          console.log('ℹ️ Watch annotation has video_file_url, skipping AI content fetch');
        }
      }
      setWatchContent(null);
      setWatchError(null);
    }
  }, [isOpen, item?.type, item?.annotation_id, item?.video_file_url, videoUrl]);

  // Auto-play video when modal opens and video is available
  useEffect(() => {
    if (isOpen && videoRef.current && (item?.type === 'Video' || item?.video_file_url || videoUrl)) {
      // Small delay to ensure video element is fully rendered
      const timer = setTimeout(() => {
        if (videoRef.current) {
          try {
            // Muted autoplay is most likely to be allowed by browsers.
            videoRef.current.muted = true;
          } catch {
            // ignore
          }
          videoRef.current.play().catch((error) => {
            // Autoplay was prevented by browser policy (user interaction required)
            // This is expected behavior - user can still click play manually
            // Silently handle the error
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, item?.type, item?.video_file_url, videoUrl]);

  // Fetch practice questions from backend if this is a Practice annotation from backend
  useEffect(() => {
    if (isOpen && item?.type === 'Practice' && item?.annotation_id) {
      setLoadingQuestions(true);
      setQuestionsError(null);
      setPracticeQuestions(null);

      chapterAnnotationsAPI.getPracticeQuestions(item.annotation_id)
        .then((data) => {
          // Transform backend format to frontend format
          const transformedQuestions = data.questions.map((q, idx) => {
            // Ensure correctIndex is a number (handle string conversion if needed)
            const correctIndex = typeof q.correctIndex === 'number'
              ? q.correctIndex
              : parseInt(q.correctIndex, 10);

            // Validate correctIndex is within bounds
            if (isNaN(correctIndex) || correctIndex < 0 || correctIndex >= (q.options?.length || 0)) {
              console.error(`Invalid correctIndex for question ${idx + 1}:`, q.correctIndex, 'Options:', q.options);
            }

            return {
              id: q.id || `q${idx + 1}`,
              question: q.question,
              options: q.options,
              correctIndex: correctIndex,
              explanation: q.explanation
            };
          });

          setPracticeQuestions(transformedQuestions);
        })
        .catch((error) => {
          console.error('Error fetching practice questions:', error);
          setQuestionsError('Failed to load questions. Please try again.');
          // Fallback to item.questions if available
          if (item.questions) {
            setPracticeQuestions(item.questions);
          }
        })
        .finally(() => {
          setLoadingQuestions(false);
        });
    } else if (isOpen && item?.type === 'Practice' && item?.questions) {
      // Use questions from item if no annotation_id (hardcoded activities)
      setPracticeQuestions(item.questions);
    }
  }, [isOpen, item]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setActivityState(isAlreadyCompleted ? 'completed' : 'idle');
      setTimer(0);
      setSelectedAnswers({});
      setClickedElements(new Set());
      setPracticeSubmitted(false);
      setPracticeAttempts({});
      setShowCorrectAnswers({});
      // Reset scoring tracking
      setPerformanceScore(null);
      setCompletionTime(null);
      setIsFirstTry(true);
      setVideoWatchPercentage(null);
      startTimeRef.current = Date.now();
    }
  }, [isOpen, isAlreadyCompleted]);

  // Track completion time
  useEffect(() => {
    if (isOpen && activityState === 'in-progress' && startTimeRef.current) {
      const interval = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setCompletionTime(elapsed);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isOpen, activityState]);

  // Video timer countdown
  useEffect(() => {
    if (item?.type === 'Watch' && activityState === 'in-progress') {
      const interval = setInterval(() => {
        setTimer(prev => {
          const newTime = prev + 1;
          // Update video watch percentage
          if (item.durationSeconds > 0) {
            const percentage = (newTime / item.durationSeconds) * 100;
            setVideoWatchPercentage(percentage);
          }

          if (newTime >= item.durationSeconds) {
            setActivityState('completed');
            if (!isAlreadyCompleted) {
              // Calculate completion time
              const elapsed = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : null;
              setCompletionTime(elapsed);

              // Call onComplete with scoring data (100% watched)
              handleActivityComplete(item.id, item.xpReward, {
                video_watch_percentage: 100,
                completion_time_seconds: elapsed,
                estimated_duration_seconds: item.durationSeconds,
              });
            }
            clearInterval(interval);
            return item.durationSeconds;
          }
          return newTime;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [activityState, item, isAlreadyCompleted]);

  const handleStartVideo = () => {
    setActivityState('in-progress');
  };

  const handleSimulationClick = (elementId) => {
    const newClicked = new Set(clickedElements);
    newClicked.add(elementId);
    setClickedElements(newClicked);

    // Check if all 3 elements clicked
    if (newClicked.size >= 3) {
      setActivityState('completed');
      if (!isAlreadyCompleted) {
        // Calculate completion time
        const elapsed = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : null;
        setCompletionTime(elapsed);

        // Call onComplete with scoring data
        handleActivityComplete(item.id, item.xpReward, {
          is_first_try: isFirstTry,
          completion_time_seconds: elapsed,
          estimated_duration_seconds: item.durationSeconds,
        });
      }
    } else {
      // Not first try anymore if they click again
      setIsFirstTry(false);
    }
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    // Only allow changing answers if not yet submitted
    if (!practiceSubmitted) {
      // Ensure optionIndex is stored as a number
      const optionIndexNum = typeof optionIndex === 'number' ? optionIndex : parseInt(optionIndex, 10);
      setSelectedAnswers(prev => ({
        ...prev,
        [questionId]: optionIndexNum
      }));
    }
  };

  const handleSubmitPractice = () => {
    // Use practiceQuestions from backend if available, otherwise fallback to item.questions
    const questions = practiceQuestions || item.questions;
    // Check if all questions answered
    const allAnswered = questions?.every(q => selectedAnswers[q.id] !== undefined);
    if (allAnswered) {
      setPracticeSubmitted(true);

      // Track attempts and show correct answers for wrong answers
      const newAttempts = { ...practiceAttempts };
      const newShowCorrect = { ...showCorrectAnswers };

      questions.forEach(q => {
        const userAnswer = selectedAnswers[q.id];
        // Ensure both values are numbers for comparison
        const userAnswerNum = userAnswer !== undefined
          ? (typeof userAnswer === 'number' ? userAnswer : parseInt(userAnswer, 10))
          : undefined;
        const correctIndexNum = typeof q.correctIndex === 'number'
          ? q.correctIndex
          : parseInt(q.correctIndex, 10);

        // Validate correctIndex is valid
        if (isNaN(correctIndexNum) || correctIndexNum < 0 || correctIndexNum >= (q.options?.length || 0)) {
          console.error(`Invalid correctIndex for question ${q.id}:`, correctIndexNum, 'Options count:', q.options?.length);
        }

        const isCorrect = userAnswerNum !== undefined && !isNaN(userAnswerNum) && userAnswerNum === correctIndexNum;

        if (!isCorrect && userAnswerNum !== undefined) {
          // Increment attempt count
          newAttempts[q.id] = (newAttempts[q.id] || 0) + 1;

          // Show correct answer if this is the second wrong attempt
          if (newAttempts[q.id] >= 2) {
            newShowCorrect[q.id] = true;
          }
        }
      });

      setPracticeAttempts(newAttempts);
      setShowCorrectAnswers(newShowCorrect);

      // Check if all answers are correct (with type-safe comparison)
      const allCorrect = questions.every(q => {
        const userAnswer = selectedAnswers[q.id];
        if (userAnswer === undefined) {
          return false;
        }
        const userAnswerNum = typeof userAnswer === 'number' ? userAnswer : parseInt(userAnswer, 10);
        const correctIndexNum = typeof q.correctIndex === 'number' ? q.correctIndex : parseInt(q.correctIndex, 10);
        const isCorrect = !isNaN(userAnswerNum) && userAnswerNum === correctIndexNum;
        return isCorrect;
      });
      if (allCorrect) {
        setActivityState('completed');
        if (!isAlreadyCompleted) {
          // Calculate performance score
          const totalQuestions = questions.length;
          const correctAnswers = questions.filter(q => {
            const userAnswer = selectedAnswers[q.id];
            const userAnswerNum = typeof userAnswer === 'number' ? userAnswer : parseInt(userAnswer, 10);
            const correctIndexNum = typeof q.correctIndex === 'number' ? q.correctIndex : parseInt(q.correctIndex, 10);
            return !isNaN(userAnswerNum) && userAnswerNum === correctIndexNum;
          }).length;
          const score = (correctAnswers / totalQuestions) * 100;
          setPerformanceScore(score);

          // Calculate completion time
          const elapsed = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : null;
          setCompletionTime(elapsed);

          // Call onComplete with scoring data
          handleActivityComplete(item.id, item.xpReward, {
            performance_score: score,
            completion_time_seconds: elapsed,
            estimated_duration_seconds: item.durationSeconds,
          });
        }
      }
    }
  };

  const handleRetakePractice = () => {
    setSelectedAnswers({});
    setPracticeSubmitted(false);
    setPracticeAttempts({});
    setShowCorrectAnswers({});
    setActivityState('idle');
  };

  const handleMarkAsRead = () => {
    setActivityState('completed');
    if (!isAlreadyCompleted) {
      // Calculate completion time
      const elapsed = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : null;
      setCompletionTime(elapsed);

      // Call onComplete with scoring data
      handleActivityComplete(item.id, item.xpReward, {
        completion_time_seconds: elapsed,
        estimated_duration_seconds: item.durationSeconds,
      });
    }
  };

  // Handle activity completion with backend API call
  const handleActivityComplete = async (activityId, xpReward, scoringData = {}) => {
    try {
      // Debug: Log item structure to understand type
      console.log('🎯 handleActivityComplete called - item:', item, 'item.type:', item?.type, 'item.annotation_type:', item?.annotation_type);

      // If chapterId and annotation_id are available, call the backend API
      if (chapterId && item?.annotation_id) {
        const completionData = await userActivitiesAPI.completeActivity(
          item.annotation_id,
          chapterId,
          scoringData
        );

        // Use rewards from backend response
        const earnedXP = completionData.xp_earned || xpReward;
        const earnedGems = completionData.gems_earned || 0;

        // Refresh points context
        await refreshPoints();

        // Trigger floating animations
        if (earnedXP > 0) {
          triggerFloatingAnimation('xp', earnedXP);
        }
        if (earnedGems > 0) {
          triggerFloatingAnimation('gems', earnedGems);
        }

        // Call parent's onComplete with backend rewards
        if (onComplete) {
          onComplete(activityId, earnedXP, earnedGems);
        }

        // Trigger global celebration
        // Get activity type from item.type or item.annotation_type (fallback)
        const rawType = item.type || item.annotation_type || null;
        // Normalize activity type to ensure proper matching (case-insensitive)
        const normalizedType = rawType ? String(rawType).trim().charAt(0).toUpperCase() + String(rawType).trim().slice(1).toLowerCase() : null;
        console.log('🎉 Dispatching celebration event - rawType:', rawType, 'normalizedType:', normalizedType, 'item.type:', item.type, 'item.annotation_type:', item.annotation_type);

        // Only dispatch if we have a valid activity type
        if (!normalizedType) {
          console.warn('⚠️ Cannot dispatch celebration event - no activity type found. item:', item);
        } else {
          const eventDetail = {
            activityId: activityId,
            isAlreadyCompleted: false,
            pointsEarned: earnedXP,
            gemsEarned: earnedGems,
            activityType: normalizedType
          };
          console.log('🎉 Event detail being dispatched:', eventDetail);
          window.dispatchEvent(new CustomEvent('activity-completed', { detail: eventDetail }));
        }

        // Show success toast with rewards
        toast.success(`🎉 Activity completed! +${earnedXP} XP${earnedGems > 0 ? `, +${earnedGems} Gems` : ''}`, {
          duration: 1000
        });
      } else {
        // Fallback to original behavior if no backend integration
        if (onComplete) {
          onComplete(activityId, xpReward);
        }
        // Normalize activity type to ensure proper matching (case-insensitive)
        const rawType = item.type || item.annotation_type || null;
        const normalizedType = rawType ? String(rawType).trim().charAt(0).toUpperCase() + String(rawType).trim().slice(1).toLowerCase() : null;
        console.log('🎉 Dispatching celebration event (fallback) - rawType:', rawType, 'normalizedType:', normalizedType);
        if (normalizedType) {
          window.dispatchEvent(new CustomEvent('activity-completed', {
            detail: { activityId: activityId, isAlreadyCompleted: false, pointsEarned: xpReward, activityType: normalizedType }
          }));
        } else {
          console.warn('⚠️ Cannot dispatch celebration event (fallback) - no activity type found');
        }
      }
    } catch (error) {
      console.error('Error completing activity:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Request data:', {
        annotation_id: item?.annotation_id,
        chapter_id: chapterId,
        scoringData
      });

      // Show more detailed error message
      const errorMessage = error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        'Failed to save completion. Please try again.';

      // Fallback to original behavior on error
      if (onComplete) {
        onComplete(activityId, xpReward);
      }

      // Still trigger celebration even if backend fails
      const rawType = item.type || item.annotation_type || null;
      const normalizedType = rawType ? String(rawType).trim().charAt(0).toUpperCase() + String(rawType).trim().slice(1).toLowerCase() : null;
      console.log('🎉 Dispatching celebration event (error fallback) - rawType:', rawType, 'normalizedType:', normalizedType);
      if (normalizedType) {
        window.dispatchEvent(new CustomEvent('activity-completed', {
          detail: {
            activityId: activityId,
            isAlreadyCompleted: false,
            pointsEarned: xpReward,
            gemsEarned: 0,
            activityType: normalizedType
          }
        }));
      } else {
        console.warn('⚠️ Cannot dispatch celebration event (error fallback) - no activity type found');
      }

      toast.error(errorMessage);
    }
  };


  if (!item) return null;

  const effectiveVideoUrl = (item?.youtube_url || item?.video_file_url || videoUrl || null);

  const videoProgress = item.durationSeconds > 0 ? (timer / item.durationSeconds) * 100 : 0;
  // Use practiceQuestions from backend if available, otherwise fallback to item.questions
  const questions = practiceQuestions || item.questions;
  const allQuestionsAnswered = questions?.every(q => selectedAnswers[q.id] !== undefined);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent ref={modalContentRef} style={{ display: 'flex' }} className="max-w-4xl max-h-[90vh] flex-col overflow-hidden p-0 bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))]">
          <DialogTitle className="sr-only">
            {item.type === 'Curiosity' && curiosityContent?.title
              ? curiosityContent.title
              : item.title || (item.type === 'Watch' ? 'Learn & Discover Activity' : `${item.type} Activity`)}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {item.type === 'Curiosity' && curiosityContent?.intro
              ? curiosityContent.intro
              : item.description || (item.type === 'Watch' ? 'Complete this learn & discover activity' : `Complete this ${item.type.toLowerCase()} activity`)}
          </DialogDescription>
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between mb-3 sm:mb-6 px-2 sm:px-6 pt-2 sm:pt-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br ${colorGradient} flex items-center justify-center shadow-xl`}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
              </div>
              <div>
                <div className="text-xs sm:text-sm lg:text-base font-bold text-[hsl(var(--teal-vivid))] uppercase tracking-wide">
                  {item.type === 'Watch' ? 'Learn & Discover' : item.type}
                </div>
                <h2 className="text-base sm:text-xl lg:text-2xl font-bold text-white">{item.title}</h2>
                <p className="text-gray-300 text-xs sm:text-sm">{item.duration}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <BookmarkIcon
                chapterId={chapterId}
                sectionName={item.section_name || 'Learning Pack'}
                sectionNumber={item.section_number}
                bookmarkType="activity"
                contentId={item.id || item.annotation_id}
                contentData={item}
                initialBookmarked={!!viewerBookmark}
                bookmarkId={viewerBookmark?.id}
                onToggle={(isBookmarked, newBookmark) => {
                  console.log('🔄 ActivityModal BookmarkIcon Toggle:', { isBookmarked, newBookmark });
                  if (isBookmarked) {
                    setViewerBookmark(newBookmark);
                    if (onBookmarkChange) {
                      console.log('🔄 Calling onBookmarkChange(true)');
                      onBookmarkChange(true, newBookmark);
                    } else {
                      console.warn('⚠️ onBookmarkChange prop missing in ActivityModal');
                    }
                  } else {
                    setViewerBookmark(null);
                    if (onBookmarkChange) {
                      console.log('🔄 Calling onBookmarkChange(false)');
                      onBookmarkChange(false, null);
                    } else {
                      console.warn('⚠️ onBookmarkChange prop missing in ActivityModal');
                    }
                  }
                }}
              />
              <Button
                onClick={handleClose}
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground h-8 sm:h-10 w-8 sm:w-10"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </div>

          {/* Activity Content - Scrollable */}
          <div className="flex-1 min-h-0 overflow-y-auto space-y-3 sm:space-y-4 lg:space-y-6 px-3 sm:px-6 pb-3 sm:pb-6">
            <p className="text-gray-300 leading-relaxed text-xs sm:text-sm lg:text-base">{item.description}</p>

            {/* WATCH: AI-generated Interactive Content (Learn & Discover) - NO videos */}
            {item.type === 'Watch' && (
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                {/* Only AI-generated Watch Content (interactive_animation, infographic, story, timeline, concept_map) */}
                {loadingWatch ? (
                  <div className="flex items-center justify-center py-6 sm:py-12">
                    <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-blue-400" />
                    <span className="ml-2 sm:ml-3 text-gray-300 text-xs sm:text-sm">Loading watch content...</span>
                  </div>
                ) : watchError ? (
                  <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3 sm:p-4 text-center">
                    <p className="text-red-400 text-xs sm:text-sm lg:text-base">{watchError}</p>
                    <p className="text-gray-300 text-xs sm:text-sm mt-2">Please try again later or contact support if the issue persists.</p>
                  </div>
                ) : watchContent && watchContent.content_data ? (
                  <WatchContentRenderer
                    watchContent={watchContent}
                    onComplete={() => {
                      setActivityState('completed');
                      if (!isAlreadyCompleted) {
                        const elapsed = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : null;
                        setCompletionTime(elapsed);
                        handleActivityComplete(item.id, item.xpReward, {
                          completion_time_seconds: elapsed,
                          estimated_duration_seconds: item.durationSeconds,
                        });
                      }
                    }}
                  />
                ) : watchContent && !watchContent.content_data ? (
                  <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-4 text-center">
                    <p className="text-yellow-400 mb-2">Watch content structure is incomplete</p>
                    <p className="text-gray-400 text-sm">Content data is missing. The content may still be generating.</p>
                  </div>
                ) : (
                  <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-6 text-center">
                    <BookOpen className="w-16 h-16 mx-auto mb-4 text-blue-400 opacity-75" />
                    <p className="text-blue-400 font-semibold text-lg mb-2">Learn & Discover Content</p>
                    <p className="text-gray-300 text-sm mb-4">
                      AI-generated interactive content is being prepared for this activity.
                    </p>
                    <p className="text-gray-400 text-xs">
                      This content will include interactive animations, infographics, stories, timelines, or concept maps.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* VIDEO: Uploaded Video Only */}
            {item.type === 'Video' && (
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                {/* Priority 1: Uploaded Video (for Video type) */}
                {effectiveVideoUrl ? (
                  <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                    {/* Check if it's a YouTube URL - use YouTube player */}
                    {item?.youtube_url || (effectiveVideoUrl && isYouTubeUrl(effectiveVideoUrl)) ? (
                      <div className="bg-black rounded-lg aspect-video flex items-center justify-center relative overflow-hidden">
                        <div className="absolute top-3 right-3 z-20">
                          <select
                            value={playbackRate}
                            onChange={(e) => setPlaybackRate(Number(e.target.value))}
                            className="bg-[hsl(var(--card-bg))] text-sm !text-white border border-[hsl(var(--card-border))] rounded px-2 py-1"
                            title="Playback speed"
                          >
                            <option value={0.5}>0.5x</option>
                            <option value={0.75}>0.75x</option>
                            <option value={1}>1x</option>
                            <option value={1.25}>1.25x</option>
                            <option value={1.5}>1.5x</option>
                          </select>
                        </div>
                        <YouTubeVideoPlayer
                          youtubeUrl={item?.youtube_url || effectiveVideoUrl}
                          playbackRate={playbackRate}
                          onEnded={() => {
                            setActivityState('completed');
                            if (!isAlreadyCompleted) {
                              const elapsed = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : null;
                              setCompletionTime(elapsed);
                              handleActivityComplete(item.id, item.xpReward, {
                                video_watch_percentage: 100,
                                completion_time_seconds: elapsed,
                                estimated_duration_seconds: item.durationSeconds,
                              });
                            }
                          }}
                          onTimeUpdate={(percentage) => {
                            setVideoWatchPercentage(percentage);
                          }}
                        />
                      </div>
                    ) : effectiveVideoUrl ? (
                      <div className="bg-black rounded-lg aspect-video flex items-center justify-center relative overflow-hidden">
                        <div className="absolute top-3 right-3 z-20">
                          <select
                            value={playbackRate}
                            onChange={(e) => setPlaybackRate(Number(e.target.value))}
                            className="bg-[hsl(var(--card-bg))] text-sm text-foreground border border-[hsl(var(--card-border))] rounded px-2 py-1"
                            title="Playback speed"
                          >
                            <option value={0.5}>0.5x</option>
                            <option value={0.75}>0.75x</option>
                            <option value={1}>1x</option>
                            <option value={1.25}>1.25x</option>
                            <option value={1.5}>1.5x</option>
                          </select>
                        </div>
                        <video
                          ref={videoRef}
                          src={effectiveVideoUrl}
                          controls
                          autoPlay
                          playsInline
                          className="w-full h-full"
                          onTimeUpdate={(e) => {
                            // Track video watch percentage
                            const video = e.target;
                            if (video.duration > 0) {
                              const percentage = (video.currentTime / video.duration) * 100;
                              setVideoWatchPercentage(percentage);
                            }
                          }}
                          onEnded={() => {
                            setActivityState('completed');
                            if (!isAlreadyCompleted) {
                              // Calculate completion time
                              const elapsed = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : null;
                              setCompletionTime(elapsed);

                              // Call onComplete with scoring data (100% watched)
                              handleActivityComplete(item.id, item.xpReward, {
                                video_watch_percentage: 100,
                                completion_time_seconds: elapsed,
                                estimated_duration_seconds: item.durationSeconds,
                              });
                            }
                          }}
                          onError={(e) => {
                            console.error('Video playback error:', e);
                            const video = e.target;
                            const errorMsg = video.error ?
                              `Error code: ${video.error.code} - ${getVideoErrorMessage(video.error.code)}` :
                              'Failed to load video';
                            toast.error(`Video playback error: ${errorMsg}. Please check if the video file exists and is accessible.`);
                          }}
                          onLoadStart={() => {
                            console.log('Video loading started:', effectiveVideoUrl);
                          }}
                          onLoadedData={() => {
                            console.log('Video loaded successfully');
                          }}
                          onCanPlay={() => {
                            console.log('Video can start playing');
                          }}
                        >
                          <source src={effectiveVideoUrl} type="video/mp4" />
                          <source src={effectiveVideoUrl} type="video/webm" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : null}
                    {activityState === 'completed' && effectiveVideoUrl && (
                      <div className="text-center">
                        <Check className="w-8 h-8 mx-auto mb-2 text-green-400" />
                        <div className="text-lg font-bold text-white">Video Completed!</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-6 text-center">
                    <Video className="w-16 h-16 mx-auto mb-4 text-yellow-400 opacity-75" />
                    <p className="text-yellow-400 font-semibold text-lg mb-2">Video Not Available</p>
                    <p className="text-gray-300 text-sm mb-4">
                      This Video annotation doesn't have a video file uploaded yet.
                    </p>
                    <div className="bg-gray-800/50 rounded-lg p-3 text-left">
                      <p className="text-gray-400 text-xs mb-1">To add a video:</p>
                      <ol className="text-gray-300 text-xs space-y-1 list-decimal list-inside">
                        <li>Go to SuperAdmin Portal → Learning Path</li>
                        <li>Find this annotation (ID: {item.id})</li>
                        <li>Click the Video icon to upload a video file</li>
                        <li>Save the annotations</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* EXPLORE: Interactive Simulation */}
            {item.type === 'Explore' && (
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                {loadingSimulation ? (
                  <div className="flex flex-col items-center justify-center py-6 sm:py-12">
                    <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-teal-400" />
                    <span className="ml-2 sm:ml-3 text-gray-300 mt-2 sm:mt-4 text-xs sm:text-sm">Loading simulation...</span>
                  </div>
                ) : simulationError ? (
                  <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 sm:p-4 text-center">
                    <p className="text-red-400 mb-3 sm:mb-4 text-xs sm:text-sm lg:text-base">{simulationError}</p>
                    {/* Fallback to old mockup if simulation fails to load */}
                    <div className="bg-gradient-to-br from-[hsl(var(--card-bg))] to-[hsl(var(--card-bg))]/80 border-2 border-[hsl(var(--card-border))] rounded-lg p-4 sm:p-8">
                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white mb-4 sm:mb-6 text-center">
                        Click all interactions to complete!
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        {['PUSH', 'PULL', 'HIT'].map((action, idx) => {
                          const elementId = `sim-${idx}`;
                          const isClicked = clickedElements.has(elementId);
                          return (
                            <Button
                              key={elementId}
                              onClick={() => handleSimulationClick(elementId)}
                              disabled={isClicked}
                              className={`h-32 text-xl font-bold ${isClicked
                                ? 'bg-green-600 hover:bg-green-600 cursor-not-allowed'
                                : 'bg-gradient-to-br from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700'
                                }`}
                            >
                              {isClicked ? <Check className="w-8 h-8" /> : action}
                            </Button>
                          );
                        })}
                      </div>
                      <div className="mt-4 text-center text-gray-300">
                        {clickedElements.size} / 3 interactions completed
                      </div>
                    </div>
                  </div>
                ) : exploreSimulation ? (
                  <div className="space-y-4">
                    {/* Simulation header */}
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {exploreSimulation.title || 'Explore Activity'}
                      </h3>
                      {exploreSimulation.description && (
                        <p className="text-gray-300 text-sm">
                          {exploreSimulation.description}
                        </p>
                      )}
                    </div>

                    {/* Render simulation based on type */}
                    {(() => {
                      // Ensure simulation_data is an object
                      let simulationData = exploreSimulation.simulation_data;

                      // Parse if it's a string
                      if (typeof simulationData === 'string') {
                        try {
                          simulationData = JSON.parse(simulationData);
                        } catch (e) {
                          console.error('Error parsing simulation_data:', e);
                          return (
                            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-center">
                              <p className="text-red-400">Error parsing simulation data. Please regenerate the content.</p>
                            </div>
                          );
                        }
                      }

                      // Validate simulation_data structure
                      if (!simulationData || typeof simulationData !== 'object') {
                        return (
                          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-center">
                            <p className="text-red-400">Invalid simulation data format. Please regenerate the content.</p>
                          </div>
                        );
                      }

                      // Render the simulation
                      return (
                        <SimulationRenderer
                          simulationType={exploreSimulation.simulation_type}
                          simulationData={simulationData}
                          onComplete={() => {
                            setActivityState('completed');
                            if (!isAlreadyCompleted) {
                              // Calculate completion time
                              const elapsed = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : null;
                              setCompletionTime(elapsed);

                              // Call onComplete with scoring data (first try if all elements clicked on first attempt)
                              handleActivityComplete(item.id, item.xpReward, {
                                is_first_try: isFirstTry,
                                completion_time_seconds: elapsed,
                                estimated_duration_seconds: item.durationSeconds,
                              });
                            }
                          }}
                        />
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No simulation available</p>
                  </div>
                )}
              </div>
            )}

            {/* PRACTICE: Quiz Questions */}
            {item.type === 'Practice' && (
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                {loadingQuestions ? (
                  <div className="flex items-center justify-center py-6 sm:py-12">
                    <div className="text-center">
                      <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-orange-500 mx-auto mb-3 sm:mb-4" />
                      <p className="text-gray-300 text-xs sm:text-sm">Loading practice questions...</p>
                    </div>
                  </div>
                ) : questionsError ? (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 sm:p-4 text-center">
                    <p className="text-red-400 text-xs sm:text-sm lg:text-base">{questionsError}</p>
                    {item.questions && (
                      <p className="text-gray-400 text-xs sm:text-sm mt-2">Using default questions</p>
                    )}
                  </div>
                ) : questions && questions.length > 0 ? (
                  questions.map((question, qIdx) => {
                    const userAnswer = selectedAnswers[question.id];
                    // Type-safe comparison
                    const userAnswerNum = typeof userAnswer === 'number' ? userAnswer : (userAnswer !== undefined ? parseInt(userAnswer, 10) : undefined);
                    const correctIndexNum = typeof question.correctIndex === 'number' ? question.correctIndex : parseInt(question.correctIndex, 10);
                    const isCorrect = userAnswerNum === correctIndexNum;
                    const isWrong = practiceSubmitted && userAnswer !== undefined && !isCorrect;
                    const shouldShowCorrect = showCorrectAnswers[question.id] || (practiceSubmitted && isCorrect);
                    const attempts = practiceAttempts[question.id] || 0;

                    return (
                      <div key={question.id} className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-6">
                        <h3 className="font-bold text-white mb-4">
                          Q{qIdx + 1}: {question.question}
                        </h3>
                        <div className="space-y-2">
                          {question.options.map((option, optIdx) => {
                            const isSelected = userAnswerNum === optIdx;
                            const isCorrectOption = optIdx === correctIndexNum;
                            const showAsCorrect = practiceSubmitted && (shouldShowCorrect && isCorrectOption);
                            const showAsWrong = practiceSubmitted && isWrong && isSelected;

                            return (
                              <div key={optIdx} className="relative">
                                <Button
                                  onClick={() => handleAnswerSelect(question.id, optIdx)}
                                  disabled={practiceSubmitted}
                                  variant="outline"
                                  className={`w-full justify-start text-left h-auto py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm lg:text-base ${showAsCorrect
                                    ? 'bg-green-600/30 border-green-500 text-green-200'
                                    : showAsWrong
                                      ? 'bg-red-600/30 border-red-500 text-red-200'
                                      : isSelected
                                        ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white'
                                        : 'bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--sidebar-hover))] text-gray-200'
                                    } ${practiceSubmitted ? 'cursor-not-allowed opacity-90' : ''}`}
                                >
                                  <div className="flex items-center gap-2 sm:gap-3">
                                    <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center ${showAsCorrect
                                      ? 'border-green-400 bg-green-400'
                                      : showAsWrong
                                        ? 'border-red-400 bg-red-400'
                                        : isSelected
                                          ? 'border-white bg-white'
                                          : 'border-muted-foreground'
                                      }`}>
                                      {showAsCorrect && <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />}
                                      {showAsWrong && <X className="w-3 h-3 sm:w-4 sm:h-4 text-white" />}
                                      {!practiceSubmitted && isSelected && <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-[hsl(var(--primary))]" />}
                                    </div>
                                    <span>{option}</span>
                                    {showAsCorrect && (
                                      <span className="ml-auto text-xs font-semibold">✓ Correct</span>
                                    )}
                                    {showAsWrong && (
                                      <span className="ml-auto text-xs font-semibold">✗ Wrong</span>
                                    )}
                                  </div>
                                </Button>
                              </div>
                            );
                          })}
                        </div>

                        {/* Show feedback after submission */}
                        {practiceSubmitted && (
                          <div className="mt-3 sm:mt-4 space-y-2">
                            {isCorrect ? (
                              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-2 sm:p-3">
                                <p className="text-green-300 text-xs sm:text-sm lg:text-base font-semibold">✓ Correct! Well done!</p>
                                {question.explanation && (
                                  <p className="text-green-200 text-xs sm:text-sm mt-1">{question.explanation}</p>
                                )}
                              </div>
                            ) : isWrong ? (
                              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-2 sm:p-3">
                                <p className="text-red-300 text-xs sm:text-sm lg:text-base font-semibold">✗ Incorrect answer</p>
                                {attempts < 2 ? (
                                  <div className="mt-2">
                                    <p className="text-yellow-300 text-xs sm:text-sm font-medium mb-1">💡 Tip:</p>
                                    <p className="text-yellow-200 text-xs sm:text-sm">
                                      {question.explanation || `Think about real-life examples. For instance, when you push a shopping cart, you're applying force. Force can change an object's motion or shape.`}
                                    </p>
                                    <p className="text-gray-400 text-xs sm:text-sm mt-1">Try selecting the correct answer again!</p>
                                  </div>
                                ) : (
                                  <div className="mt-2">
                                    <p className="text-blue-300 text-xs sm:text-sm font-medium mb-1">📖 Correct Answer:</p>
                                    <p className="text-blue-200 text-xs sm:text-sm font-semibold">
                                      {question.options[question.correctIndex]}
                                    </p>
                                    {question.explanation && (
                                      <p className="text-blue-200 text-xs mt-1">{question.explanation}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 sm:p-4 text-center">
                    <p className="text-yellow-400 text-xs sm:text-sm lg:text-base">No questions available</p>
                  </div>
                )}

                {questions && questions.length > 0 && (
                  <div className="space-y-2 sm:space-y-3">
                    {!practiceSubmitted ? (
                      <Button
                        onClick={handleSubmitPractice}
                        disabled={!allQuestionsAnswered}
                        className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-4 sm:py-6 text-xs sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Submit Answers
                      </Button>
                    ) : (
                      <>
                        {activityState === 'completed' ? (
                          <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-3 sm:p-4 text-center">
                            <Check className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 mx-auto mb-2" />
                            <p className="text-green-300 font-bold text-sm sm:text-lg">Perfect! All answers correct!</p>
                            {!isAlreadyCompleted && (
                              <p className="text-green-200 text-xs sm:text-sm mt-1">+{item.xpReward} Gems earned!</p>
                            )}
                          </div>
                        ) : (
                          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 sm:p-4 text-center">
                            <p className="text-yellow-300 font-semibold text-xs sm:text-sm lg:text-base">Some answers need correction</p>
                            <p className="text-yellow-200 text-xs sm:text-sm mt-1">Review the feedback above and try again!</p>
                          </div>
                        )}
                        <Button
                          onClick={handleRetakePractice}
                          className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold py-4 sm:py-6 text-xs sm:text-lg"
                        >
                          <X className="w-5 h-5 mr-2" />
                          Retake Practice
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* CURIOSITY: Reading Content */}
            {item.type === 'Curiosity' && (
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                {loadingCuriosity ? (
                  <div className="flex items-center justify-center py-6 sm:py-12">
                    <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-yellow-400" />
                    <span className="ml-2 sm:ml-3 text-gray-300 text-xs sm:text-sm">Loading curiosity content...</span>
                  </div>
                ) : curiosityError ? (
                  <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-center">
                    <p className="text-red-400">{curiosityError}</p>
                    {item.content && (
                      <div className="mt-4 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-6 max-h-96 overflow-y-auto">
                        <div className="prose prose-invert max-w-none">
                          {item.content.split('\n\n').map((paragraph, idx) => (
                            <p key={idx} className="text-gray-200 leading-relaxed mb-4">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {(() => {
                      const curiosityTitle = curiosityContent?.title || item.title || 'Curiosity Corner';
                      const curiosityQuickIdea = curiosityContent?.intro || '';
                      const curiosityExplanation =
                        curiosityContent?.content || item.content || '';
                      const steps = ['Question', 'Quick idea', 'Explanation'];
                      const atFinalStep = curiosityStep >= 2;

                      const hashToIndex = (value, modulo) => {
                        const str = String(value ?? '');
                        let hash = 0;
                        for (let i = 0; i < str.length; i += 1) {
                          hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
                        }
                        return modulo ? hash % modulo : hash;
                      };

                      const curiosityVariant = hashToIndex(
                        item?.annotation_id ?? item?.id ?? curiosityTitle,
                        3
                      );

                      return (
                        <>
                          {/* Header */}
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center">
                                <Lightbulb className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-yellow-400 uppercase tracking-wide">
                                  CURIOSITY
                                </div>
                                <h3 className="text-2xl font-bold text-white mt-1">{curiosityTitle}</h3>
                              </div>
                            </div>
                            {curiosityContent?.reading_time && (
                              <div className="text-sm text-gray-400">{curiosityContent.reading_time} min</div>
                            )}
                          </div>

                          {/* Progress / navigation (varies by style) */}
                          {curiosityVariant === 0 && (
                            <div className="flex items-center gap-1 sm:gap-2">
                              {steps.map((label, idx) => {
                                const isActive = idx === curiosityStep;
                                const isDone = idx < curiosityStep;
                                return (
                                  <button
                                    key={label}
                                    type="button"
                                    onClick={() => setCuriosityStep(idx)}
                                    className={
                                      "px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold border transition-colors " +
                                      (isActive
                                        ? "bg-yellow-500/20 border-yellow-500/50 text-yellow-300"
                                        : isDone
                                          ? "bg-green-500/15 border-green-500/40 text-green-300"
                                          : "bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:text-white")
                                    }
                                  >
                                    {idx + 1}. {label}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {curiosityVariant === 1 && (
                            <div className="flex items-center gap-2">
                              {steps.map((label, idx) => {
                                const isActive = idx === curiosityStep;
                                const isDone = idx < curiosityStep;
                                return (
                                  <button
                                    key={label}
                                    type="button"
                                    onClick={() => setCuriosityStep(idx)}
                                    className={
                                      "flex-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold border transition-colors text-left " +
                                      (isActive
                                        ? "bg-yellow-500/15 border-yellow-500/40 text-yellow-200"
                                        : isDone
                                          ? "bg-green-500/10 border-green-500/35 text-green-200"
                                          : "bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:text-white")
                                    }
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span>
                                        {idx + 1}. {label}
                                      </span>
                                      {isDone && <Check className="w-4 h-4" />}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {curiosityVariant === 2 && (
                            <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-3">
                              <div className="grid grid-cols-3 gap-2">
                                {steps.map((label, idx) => {
                                  const isActive = idx === curiosityStep;
                                  const isDone = idx < curiosityStep;
                                  return (
                                    <button
                                      key={label}
                                      type="button"
                                      onClick={() => setCuriosityStep(idx)}
                                      className={
                                        "px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-semibold border transition-colors " +
                                        (isActive
                                          ? "bg-yellow-500/15 border-yellow-500/40 text-yellow-200"
                                          : isDone
                                            ? "bg-green-500/10 border-green-500/35 text-green-200"
                                            : "bg-transparent border-[hsl(var(--card-border))] text-gray-300 hover:text-white")
                                      }
                                    >
                                      <div className="flex items-center justify-center gap-2">
                                        <span>{idx + 1}</span>
                                        <span className="hidden sm:inline">{label}</span>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Step content */}
                          <div
                            className={
                              "bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] p-3 sm:p-6 " +
                              (curiosityVariant === 1
                                ? "rounded-2xl border-dashed"
                                : "rounded-lg")
                            }
                          >
                            {curiosityStep === 0 && (
                              <div className="space-y-3">
                                <div className="text-sm font-semibold text-gray-300">Question / Statement</div>
                                <div
                                  className={
                                    "text-white font-bold leading-snug " +
                                    (curiosityVariant === 1 ? "text-lg sm:text-2xl lg:text-3xl text-center" : "text-base sm:text-xl lg:text-2xl")
                                  }
                                >
                                  {curiosityTitle}
                                </div>
                                <div className="text-gray-400 text-sm">
                                  Think for a moment before you move ahead.
                                </div>
                              </div>
                            )}

                            {curiosityStep === 1 && (
                              <div className="space-y-3">
                                <div className="text-sm font-semibold text-gray-300">Quick idea</div>
                                {curiosityQuickIdea ? (
                                  <div
                                    className={
                                      "text-gray-200 italic leading-relaxed " +
                                      (curiosityVariant === 2
                                        ? "text-xs sm:text-base bg-yellow-500/10 border border-yellow-500/25 rounded-lg p-3 sm:p-4"
                                        : "text-sm sm:text-lg")
                                    }
                                  >
                                    {curiosityQuickIdea}
                                  </div>
                                ) : (
                                  <div className="text-gray-400">
                                    No quick idea provided for this curiosity.
                                  </div>
                                )}
                              </div>
                            )}

                            {curiosityStep >= 2 && (
                              <div className="space-y-4">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <div className="text-sm font-semibold text-gray-300">Explanation</div>
                                    <div className="text-xs text-gray-400">
                                      Reveal after learning ahead.
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setCuriosityReveal((v) => !v)}
                                    className="border-[hsl(var(--card-border))] text-white hover:text-white"
                                  >
                                    {curiosityReveal ? 'Hide' : 'Reveal'}
                                  </Button>
                                </div>

                                {curiosityReveal ? (
                                  <div
                                    className={
                                      "bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] p-3 sm:p-4 max-h-72 overflow-y-auto " +
                                      (curiosityVariant === 2 ? "rounded-xl" : "rounded-lg")
                                    }
                                  >
                                    <div className="prose prose-invert max-w-none">
                                      {curiosityExplanation ? (
                                        curiosityExplanation.split('\n\n').map((paragraph, idx) => (
                                          <p key={idx} className="text-gray-200 text-xs sm:text-sm lg:text-base leading-relaxed mb-4">
                                            {paragraph}
                                          </p>
                                        ))
                                      ) : (
                                        <p className="text-gray-400">No explanation available.</p>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-yellow-200">
                                    Explanation is hidden. Learn a bit more, then reveal it.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Navigation / completion */}
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setCuriosityStep((s) => Math.max(0, s - 1))}
                              disabled={curiosityStep === 0}
                              className="flex-1 border-[hsl(var(--card-border))] text-white hover:text-white disabled:opacity-50"
                            >
                              Back
                            </Button>

                            {!atFinalStep ? (
                              <Button
                                type="button"
                                onClick={() => setCuriosityStep((s) => Math.min(2, s + 1))}
                                className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white font-bold"
                              >
                                Next
                              </Button>
                            ) : (
                              <Button
                                onClick={handleMarkAsRead}
                                disabled={activityState === 'completed'}
                                className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {activityState === 'completed' ? (
                                  <>
                                    <Check className="w-5 h-5 mr-2" /> Marked as Read
                                  </>
                                ) : (
                                  'Mark as Read'
                                )}
                              </Button>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {/* BASIC CONCEPTS: Informative Learning Experience */}
            {item.type === 'BasicConcepts' && (
              <div className="space-y-4">
                <BasicConceptsRenderer
                  chapterId={item.chapter_id || item.chapter?.id}
                  onComplete={() => {
                    // Basic Concepts doesn't have completion in the traditional sense
                    // But we can mark it as viewed
                    // No scoring for Basic Concepts (it's learning, not a test)
                    setActivityState('completed');
                  }}
                />
              </div>
            )}

            {/* Completion Badge */}
            {activityState === 'completed' && item.type !== 'BasicConcepts' && (
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/50 rounded-lg p-3 sm:p-4 text-center">
                <div className="text-green-400 font-bold text-xs sm:text-lg lg:text-xl">
                  ✓ Activity Completed! {!isAlreadyCompleted && `+${item.xpReward} Gems`}
                </div>
              </div>
            )}
          </div>


        </DialogContent>
      </Dialog>
      {renderAnimations()}
    </>
  );
}
